import {Alert, Collapse, Grid, Group, NumberInput, Paper, Select, Stack, Switch, Table, Text, Title} from "@mantine/core";
import {useMemo, useState} from "react";
import {useParams} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../../components/Loader/LoaderScreen.tsx";
import Page from "../../../components/Page/Page.tsx";
import {useEntityTypes} from "../../../utils/api.ts";
import {buildProductionPlan, calculateProductionRequirements, formatLongProductionDuration} from "../../../utils/production-calculator.ts";
import type {CalculatorLocation, FinalRunStrategy} from "../../../utils/production-calculator.ts";
import {buildEstimateCatalogs} from "../../../utils/production-estimate/estimate.ts";
import ProductionModuleNav from "./ProductionModuleNav.tsx";
import {useProductionModule} from "./ProductionModuleContext.tsx";
import {formatDuration} from "./shared.ts";
import style from "./ProductionModuleCalculatorPage.module.css";
import ProductionCargoCarriers from "./components/ProductionCargoCarriers.tsx";

function formatNumber(value: number | null): string {
    return value === null ? 'Unknown' : value.toLocaleString(undefined, {maximumFractionDigits: 2});
}

function formatTime(min: number | null, max: number | null): string {
    if (min === null || max === null) return 'Unknown';
    return min === max ? formatDuration(min) : `${formatDuration(min)} – ${formatDuration(max)}`;
}

function positiveInteger(value: string | number, max: number): number {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? Math.min(max, parsed) : 1;
}

function ProductionModuleCalculatorPage() {
    const {moduleId} = useParams<{moduleId: string}>();
    const moduleInfo = useProductionModule();
    const [productId, setProductId] = useState<string | null>(null);
    const [totalAssets, setTotalAssets] = useState(1);
    const [advanced, setAdvanced] = useState(false);
    const [quantity, setQuantity] = useState<number | ''>('');
    const [locationCount, setLocationCount] = useState<number | ''>('');
    const [finalRunStrategy, setFinalRunStrategy] = useState<FinalRunStrategy>('spread');
    const [location, setLocation] = useState<CalculatorLocation>('range');
    const [management, setManagement] = useState('range');
    const {ships, vehicles, items, droids, materials, isPending, isError} = useEntityTypes([
        'ships', 'vehicles', 'items', 'droids', 'materials'
    ]);

    const catalogs = useMemo(() => buildEstimateCatalogs({ships, vehicles, items, droids}), [ships, vehicles, items, droids]);
    const productOptions = useMemo(() => [
        {group: 'Ships', items: ships},
        {group: 'Vehicles', items: vehicles},
        {group: 'Items', items},
        {group: 'Droids', items: droids},
    ].map((group) => ({
        group: group.group,
        items: group.items
            .filter((product) => (product.production?.prodModifier ?? 0) > 0)
            .map((product) => ({value: product.id, label: product.name}))
            .sort((left, right) => left.label.localeCompare(right.label)),
    })), [ships, vehicles, items, droids]);
    const product = [...ships, ...vehicles, ...items, ...droids].find((entry) => entry.id === productId);
    const plan = useMemo(() => buildProductionPlan(
        totalAssets, locationCount || 1, quantity || 48, finalRunStrategy
    ), [totalAssets, locationCount, quantity, finalRunStrategy]);
    const requirements = useMemo(() => product ? calculateProductionRequirements(
        product,
        plan,
        catalogs,
        materials,
        location,
        management === 'range' ? null : Number(management)
    ) : null, [product, plan, catalogs, materials, location, management]);

    const longDuration = requirements
        ? formatLongProductionDuration(requirements.totalSecondsMin, requirements.totalSecondsMax)
        : null;

    if (!moduleId) {
        return <Page><Alert color="red" title="Missing module ID">No module selected.</Alert></Page>;
    }

    return (
        <Page>
            <title>{`${moduleInfo?.label || 'Production'} - Calculator`}</title>
            <Stack gap="md">
                <Title order={1}>{moduleInfo?.label || 'Production'} - Calculator</Title>
                <ProductionModuleNav moduleId={moduleId}/>
                <Text c="dimmed">Enter how many assets you want to estimate production runs, completion time, raw materials and cargo.</Text>
                {isPending ? <LoaderScreen message="Loading production catalogs"/> : isError ? (
                    <Alert color="red" title="Calculator unavailable">Could not load production and material catalogs. Please reload to try again.</Alert>
                ) : (
                    <>
                        <Paper withBorder p="md">
                            <Stack gap="sm">
                                <Grid align="end">
                                    <Grid.Col span={{base: 12, md: 7}}>
                                        <Select
                                            label="Product"
                                            placeholder="Search ships, vehicles, items and droids"
                                            searchable
                                            clearable
                                            data={productOptions}
                                            value={productId}
                                            onChange={setProductId}
                                            nothingFoundMessage="No matching products"
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{base: 8, md: 3}}>
                                        <NumberInput label="Total assets wanted" min={1} max={1000000000} allowDecimal={false} value={totalAssets}
                                                     onChange={(value) => setTotalAssets(positiveInteger(value, 1000000000))}/>
                                    </Grid.Col>
                                    <Grid.Col span={{base: 4, md: 2}} className={style.advancedToggle}>
                                        <Switch label="Advanced" checked={advanced} onChange={(event) => setAdvanced(event.currentTarget.checked)}
                                                aria-controls="production-calculator-advanced" aria-expanded={advanced}/>
                                    </Grid.Col>
                                </Grid>
                                <Collapse expanded={advanced} id="production-calculator-advanced">
                                    <Stack gap="sm">
                                        <Group align="flex-end" gap="md">
                                            <NumberInput label="Locations (optional)" placeholder="1" w={170}
                                                         min={1} max={1000000} allowDecimal={false} value={locationCount}
                                                         onChange={(value) => setLocationCount(value === '' ? '' : positiveInteger(value, 1000000))}/>
                                            <NumberInput label="Quantity per run (optional)" placeholder="48" w={190}
                                                         min={1} max={48} allowDecimal={false} value={quantity}
                                                         onChange={(value) => setQuantity(value === '' ? '' : positiveInteger(value, 48))}/>
                                            <Select
                                                label="Location modifier"
                                                w={280}
                                                value={location}
                                                onChange={(value) => setLocation(value as CalculatorLocation)}
                                                allowDeselect={false}
                                                data={[
                                                    {value: 'range', label: 'Station–facility range (0.9–1.0)'},
                                                    {value: 'station', label: 'Station (0.9)'},
                                                    {value: 'facility', label: 'Facility (1.0)'},
                                                ]}
                                            />
                                            <Select
                                                label="Management skill"
                                                w={170}
                                                value={management}
                                                onChange={(value) => setManagement(value ?? 'range')}
                                                allowDeselect={false}
                                                data={[
                                                    {value: 'range', label: 'Unknown (0–5)'},
                                                    ...[0, 1, 2, 3, 4, 5].map((skill) => ({value: String(skill), label: String(skill)})),
                                                ]}
                                            />
                                            <Select
                                                label="Final round"
                                                w={380}
                                                value={finalRunStrategy}
                                                onChange={(value) => setFinalRunStrategy(value as FinalRunStrategy)}
                                                allowDeselect={false}
                                                data={[
                                                    {value: 'spread', label: 'Spread across all locations with smaller runs'},
                                                    {value: 'full', label: 'Keep the same quantity per run at fewer locations'},
                                                ]}
                                            />
                                        </Group>
                                        <Text size="xs" c="dimmed">
                                            Uses the planner&apos;s production formula with civilization level 0 and the standard worker modifier.
                                            Locations run in parallel, with sequential runs at each location. Quantity per run is per location (max 48). The quantity discount applies to each run&apos;s actual size.
                                            Final runs round up to whole assets; materials and cargo include any extra assets produced. Actual locations and delays can change the time.
                                        </Text>
                                    </Stack>
                                </Collapse>
                            </Stack>
                        </Paper>
                        {product && requirements ? (
                            <Grid gutter="md">
                                <Grid.Col span={{base: 12, lg: 7}}>
                                    <Stack gap="md">
                                        <Paper withBorder p="md">
                                            <Stack gap="sm">
                                                <Title order={3}>{product.name}</Title>
                                                <div className={style.stats}>
                                                    <div>
                                                        <Text size="xs" c="dimmed">Total completion time</Text>
                                                        <Text fw={600}>{formatTime(requirements.totalSecondsMin, requirements.totalSecondsMax)}</Text>
                                                        {longDuration ? <Text size="xs" c="dimmed">{longDuration}</Text> : null}
                                                    </div>
                                                    <div>
                                                        <Text size="xs" c="dimmed">Planned assets</Text>
                                                        <Text fw={600}>{formatNumber(plan.produced)}</Text>
                                                        {plan.surplus > 0 ? <Text size="xs" c="dimmed">+{formatNumber(plan.surplus)} from rounding</Text> : null}
                                                    </div>
                                                    <div>
                                                        <Text size="xs" c="dimmed">Total runs</Text>
                                                        <Text fw={600}>{formatNumber(plan.runs)}</Text>
                                                    </div>
                                                </div>
                                                <Table.ScrollContainer minWidth={500}>
                                                    <Table withTableBorder verticalSpacing={4} fz="sm">
                                                        <Table.Thead><Table.Tr>
                                                            <Table.Th>Phase</Table.Th><Table.Th>Rounds</Table.Th><Table.Th>Locations</Table.Th>
                                                            <Table.Th>Per run</Table.Th><Table.Th>Produced</Table.Th><Table.Th>Time per round</Table.Th>
                                                        </Table.Tr></Table.Thead>
                                                        <Table.Tbody>
                                                            {requirements.stages.map((stage) => (
                                                                <Table.Tr key={stage.phase}>
                                                                    <Table.Td>{stage.phase === 'full' ? 'Full rounds' : 'Final round'}</Table.Td>
                                                                    <Table.Td>{formatNumber(stage.rounds)}</Table.Td><Table.Td>{formatNumber(stage.locations)}</Table.Td>
                                                                    <Table.Td>{formatNumber(stage.quantity)}</Table.Td>
                                                                    <Table.Td>{formatNumber(stage.rounds * stage.locations * stage.quantity)}</Table.Td>
                                                                    <Table.Td>{formatTime(stage.secondsMin, stage.secondsMax)}</Table.Td>
                                                                </Table.Tr>
                                                            ))}
                                                        </Table.Tbody>
                                                    </Table>
                                                </Table.ScrollContainer>
                                                {requirements.estimateError ? <Alert color="yellow" title="Time estimate unavailable">{requirements.estimateError}</Alert> : null}
                                            </Stack>
                                        </Paper>
                                        <Paper withBorder p="md">
                                            <Stack gap="sm">
                                                <Title order={3}>Materials</Title>
                                                {requirements.materials.length === 0 ? (
                                                    <Alert color="yellow" title="Materials unavailable">This product has no material requirements in the catalog. Material and cargo totals are unknown.</Alert>
                                                ) : (
                                                    <Table.ScrollContainer minWidth={500}>
                                                        <Table striped withTableBorder verticalSpacing={4} fz="sm">
                                                            <Table.Thead><Table.Tr>
                                                                <Table.Th>Material</Table.Th><Table.Th>Per asset</Table.Th><Table.Th>All runs</Table.Th>
                                                                <Table.Th>Weight (T)</Table.Th><Table.Th>Volume (m³)</Table.Th>
                                                            </Table.Tr></Table.Thead>
                                                            <Table.Tbody>
                                                                {requirements.materials.map((row) => (
                                                                    <Table.Tr key={row.materialId}>
                                                                        <Table.Td>{row.name}</Table.Td><Table.Td>{formatNumber(row.perAsset)}</Table.Td>
                                                                        <Table.Td>{formatNumber(row.total)}</Table.Td><Table.Td>{formatNumber(row.weight)}</Table.Td>
                                                                        <Table.Td>{formatNumber(row.volume)}</Table.Td>
                                                                    </Table.Tr>
                                                                ))}
                                                            </Table.Tbody>
                                                        </Table>
                                                    </Table.ScrollContainer>
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{base: 12, lg: 5}}>
                                    <Paper withBorder p="md">
                                        <Stack gap="sm">
                                            <Title order={3}>Cargo</Title>
                                            {requirements.materials.length === 0 ? (
                                                <Text size="sm" c="dimmed">No material data, so cargo is unknown.</Text>
                                            ) : requirements.weight === null || requirements.volume === null ? (
                                                <Alert color="yellow" title="Cargo data incomplete">Some materials have no weight or volume data. Cargo totals and trips are unknown.</Alert>
                                            ) : (
                                                <>
                                                    <div className={style.stats}>
                                                        <div><Text size="xs" c="dimmed">All runs</Text><Text fw={600}>{formatNumber(requirements.weight)} T · {formatNumber(requirements.volume)} m³</Text></div>
                                                        <div><Text size="xs" c="dimmed">Per asset</Text><Text fw={600}>{formatNumber(requirements.weight / plan.produced)} T · {formatNumber(requirements.volume / plan.produced)} m³</Text></div>
                                                    </div>
                                                    <ProductionCargoCarriers weight={requirements.weight!} volume={requirements.volume!}/>
                                                </>
                                            )}
                                        </Stack>
                                    </Paper>
                                </Grid.Col>
                            </Grid>
                        ) : <Text c="dimmed">Select a product to see its production requirements.</Text>}
                    </>
                )}
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(ProductionModuleCalculatorPage);
